import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './ProductDNA.css';

// Keep local development configurable, but never make a deployed build call
// the visitor's own localhost. The Django API is mounted under /api/auth.
const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;

function ProductDNA() {
  const { productNumber: routeNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [productNumber, setProductNumber] = useState(routeNumber || searchParams.get('product') || '');
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => stopScanner(), []);

  async function verify(number = productNumber) {
    const value = number.trim();
    if (!value) return setError('Enter a product number to verify.');
    setError('');
    setStatus('loading');
    setProduct(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/products/dna/${encodeURIComponent(value)}/`);
      if (response.status === 404) throw new Error('No product record was found for that number.');
      if (!response.ok) throw new Error('Product verification is temporarily unavailable.');
      setProduct(await response.json());
      setStatus('success');
      navigate(`/product-dna/${encodeURIComponent(value)}`, { replace: true });
    } catch (requestError) {
      setStatus('error');
      setError(requestError.message);
    }
  }

  async function startScanner() {
    if (!('BarcodeDetector' in window)) {
      setError('QR scanning is not supported by this browser. Please enter the product number manually.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setScanning(true);
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const scan = async () => {
        if (!streamRef.current) return;
        const codes = await detector.detect(videoRef.current);
        if (codes[0]?.rawValue) {
          const value = codes[0].rawValue.split('/product-dna/').pop().split('?')[0];
          setProductNumber(decodeURIComponent(value));
          stopScanner();
          verify(decodeURIComponent(value));
          return;
        }
        window.requestAnimationFrame(scan);
      };
      scan();
    } catch (scannerError) {
      setError(scannerError.message || 'Camera access was not available.');
      stopScanner();
    }
  }

  function stopScanner() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  }

  return (
    <section className="product-dna-page">
      <div className="product-dna-hero">
        <p className="product-dna-eyebrow">LADYFIRST PRODUCT DNA</p>
        <h1>Verify a product’s story</h1>
        <p>Search the product number or scan its QR code to see its verified history.</p>
      </div>
      <form className="product-dna-search" onSubmit={(event) => { event.preventDefault(); verify(); }}>
        <label htmlFor="product-number">Product number</label>
        <div className="product-dna-search-row">
          <input id="product-number" value={productNumber} onChange={(event) => setProductNumber(event.target.value)} placeholder="e.g. LF-2025-000123" />
          <button type="submit" disabled={status === 'loading'}>{status === 'loading' ? 'Checking…' : 'Verify'}</button>
          <button type="button" className="secondary-button" onClick={scanning ? stopScanner : startScanner}>{scanning ? 'Stop scan' : 'Scan QR'}</button>
        </div>
        {scanning && <video className="product-dna-scanner" ref={videoRef} muted playsInline aria-label="QR code scanner" />}
      </form>
      {error && <div className="product-dna-message error" role="alert">{error}</div>}
      {product && <ProductRecord product={product} />}
      {!product && status === 'idle' && <div className="product-dna-empty"><span>✓</span><h2>Check before you buy</h2><p>Every verified record is connected to a product number and its registered ownership history.</p></div>}
      <Link className="product-dna-back" to="/">Back to Ladyfirst.me</Link>
    </section>
  );
}

function ProductRecord({ product }) {
  const status = product.verification_status || 'verified';
  return <div className="product-dna-record">
    <div className="product-dna-record-header"><div><p className="product-dna-eyebrow">PRODUCT RECORD</p><h2>{product.title || product.name}</h2><p className="product-number">{product.product_number || product.number}</p></div><span className={`verification-badge ${status}`}>● {status}</span></div>
    <div className="product-dna-gallery">{(product.images || []).map((image, index) => <img key={image.id || index} src={image.image || image.url || image} alt={`${product.title || 'Product'} ${index + 1}`} />)}</div>
    <div className="product-dna-grid"><InfoCard title="Seller" value={product.seller?.name || product.seller_name || 'Verified seller'} detail={product.seller?.phone || product.seller_phone} /><InfoCard title="Sold to" value={product.buyer?.name || product.buyer_name || 'Private buyer'} detail={product.sold_at ? `Purchased ${new Date(product.sold_at).toLocaleDateString()}` : 'Not sold yet'} /><InfoCard title="Sale" value={product.sale_price ? `${product.currency || '฿'}${product.sale_price}` : 'Not sold yet'} detail={product.sold_at ? new Date(product.sold_at).toLocaleString() : 'Currently available'} /><InfoCard title="Bidding" value={product.bidding?.status || product.status || 'No active bid'} detail={product.bidding?.highest_bid ? `Highest bid: ${product.bidding.highest_bid}` : ''} /></div>
    {product.history?.length > 0 && <div className="product-dna-history"><h3>Complete product timeline</h3>{product.history.map((event, index) => <div className="history-event" key={event.id || index}><span className="history-dot" /><div><strong>{event.title || event.type}</strong><p>{event.description || event.date}</p>{event.bidder_reference && <small>{event.bidder_reference}</small>}</div></div>)}</div>}
  </div>;
}

function InfoCard({ title, value, detail }) { return <div className="info-card"><p>{title}</p><strong>{value}</strong>{detail && <span>{detail}</span>}</div>; }

export default ProductDNA;
